/**
 * StatsSummaryCards - Summary metrics cards for statistics dashboard
 *
 * Displays key performance indicators in a grid of metric cards.
 */
import { Activity, CheckCircle2, TrendingUp, Database, Loader2 } from 'lucide-react';
import { MetricCard, MetricCardSkeleton } from '../monitoring/MetricCard';
import type { ConnectorStats } from '@/hooks/useConnectorStats';
import type { StatsTrends, TrendDelta } from './trendDeltas';
import { formatDuration } from '@/utils/formatDuration';

/**
 * Convert an absolute TrendDelta (value always ≥ 0, sign carried by direction)
 * to the signed-value format expected by MetricCard's trend prop.
 */
const toMetricTrend = (d?: TrendDelta) =>
  d ? { value: d.direction === 'down' ? -d.value : d.value, direction: d.direction, label: d.label } : undefined;

interface StatsSummaryCardsProps {
  stats: ConnectorStats | null;
  isLoading: boolean;
  trends?: StatsTrends;
}

/**
 * Format large numbers with K/M/B suffixes
 */
function formatNumber(num: number | null | undefined): string {
  if (num == null) return '0';
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(1)}B`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return num.toLocaleString('en-US');
}

/**
 * Format bytes to human-readable size (GB/TB)
 */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '0 B';
  if (bytes >= 1_099_511_627_776) {
    // 1 TB
    return `${(bytes / 1_099_511_627_776).toFixed(1)} TB`;
  }
  if (bytes >= 1_073_741_824) {
    // 1 GB
    return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  }
  if (bytes >= 1_048_576) {
    // 1 MB
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  }
  if (bytes >= 1_024) {
    // 1 KB
    return `${(bytes / 1_024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

/**
 * StatsSummaryCards component
 *
 * Displays a grid of metric cards showing key statistics.
 */
export function StatsSummaryCards({ stats, isLoading, trends }: StatsSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {/* Total Executions */}
      <MetricCard
        icon={Activity}
        label="Total Executions"
        value={stats.totalExecutions.toLocaleString('en-US')}
        description="Selected period"
        iconColor="text-info"
        trend={toMetricTrend(trends?.executions)}
      />

      {/* Success Rate */}
      <MetricCard
        icon={TrendingUp}
        label="Success Rate"
        value={`${stats.successRate.toFixed(1)}%`}
        description={`${stats.successfulExecutions} successful`}
        valueColor={
          stats.successRate >= 95
            ? 'text-success'
            : stats.successRate >= 80
            ? 'text-warning'
            : 'text-destructive'
        }
        iconColor={
          stats.successRate >= 95
            ? 'text-success'
            : stats.successRate >= 80
            ? 'text-warning'
            : 'text-destructive'
        }
        trend={toMetricTrend(trends?.successRate)}
      />

      {/* Records Processed */}
      <MetricCard
        icon={Database}
        label="Records Processed"
        value={formatNumber(stats.totalRecordsProcessed)}
        description="Total records"
        iconColor="text-violet"
      />

      {/* Data Written */}
      <MetricCard
        icon={Database}
        label="Data Written"
        value={formatBytes(stats.totalBytesWritten)}
        description="Total size"
        iconColor="text-info"
      />

      {/* Active Connectors */}
      <MetricCard
        icon={CheckCircle2}
        label="Active Connectors"
        value={stats.activeConnectors}
        description={`${stats.disabledConnectors} disabled`}
        valueColor="text-success"
        iconColor="text-success"
      />

      {/* Running Now / Avg Duration */}
      {stats.runningExecutions > 0 ? (
        <MetricCard
          icon={Loader2}
          label="Running Now"
          value={stats.runningExecutions}
          description="Active executions"
          valueColor="text-info"
          iconColor="text-info animate-spin"
        />
      ) : (
        <MetricCard
          icon={Activity}
          label="Avg Duration"
          value={formatDuration(stats.avgDurationSeconds, { emptyText: '0s' })}
          description="Per execution"
          iconColor="text-muted-foreground"
        />
      )}
    </div>
  );
}
